import React, { useState, useEffect, useMemo } from "react";
import {
  Input,
  Row,
  Card,
  Table,
  Spin,
  Button,
  Space,
  Form,
  Pagination,
} from "antd";
import { ArrowDownOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { connect, useDispatch } from "react-redux";

import MainLayout from "../../../components/layouts/enhancement/MainLayout";
import TopContent from "../../../components/layouts/enhancement/TopContent";
import cnt from "../../../services/definedConstant";
import { rightAction } from "../../../redux/actions";
import {
  labelConfig,
  staticConfig,
  apiConfig,
} from "../treasuryContractBooking.config";
import SearchLg from "@untitled-ui/icons-react/build/esm/SearchLg";
import ArrowLeft from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import ArrowRight from "@untitled-ui/icons-react/build/esm/ArrowRight";
import { BOM } from "../../../services/definedMethod";


const formatDateArray = (arr) => {
  if (!Array.isArray(arr) || arr.length < 3) return "-";
  const [y, m, d] = arr;
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

const STATUS_COLORS = {
  Booked: { bg: "#ECFDF3", color: "#027A48" },
  Settled: { bg: "#EFF8FF", color: "#175CD3" },
  Pending: { bg: "#FFFAEB", color: "#B54708" },
  Cancelled: { bg: "#FEF3F2", color: "#B42318" },
};

const StatusBadge = ({ label }) => {
  const s = STATUS_COLORS[label] || { bg: "#F2F4F7", color: "#344054" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 99,
        fontSize: 12,
        fontWeight: 600,
        background: s.bg,
        color: s.color,
      }}
    >
      {label}
    </span>
  );
};

const fmt = (val) =>
  val != null ? val.toLocaleString("en-NG", { minimumFractionDigits: 2 }) : "-";

const ManageTrade = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { userData, rightBtn, rightAction } = props;
  const { session } = userData || {};

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [originalList, setOriginalList] = useState([]);
  const [loading, setLoading] = useState(false);

  const pageSize = 10;

  const columns = [
    {
      title: (
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          Trade Date{" "}
          <ArrowDownOutlined style={{ fontSize: 14, color: "gray" }} />
        </div>
      ),
      dataIndex: "tradeDate",
      sorter: (a, b) => a.tradeDate.localeCompare(b.tradeDate),
    },
    { title: "Settlement Date", dataIndex: "settlementDate" },
    { title: "Trade Reference", dataIndex: "tradeReference" },
    { title: "Direction", dataIndex: "directionDescription" },
    { title: "Face Value", dataIndex: "faceValue", render: fmt },
    { title: "Price", dataIndex: "price", render: fmt },
    {
      title: "Yield (%)",
      dataIndex: "yield",
      render: (v) => (v != null ? `${parseFloat(v).toFixed(3)}%` : "-"),
    },
    {
      title: "Status",
      dataIndex: "statusDescription",
      render: (val) => <StatusBadge label={val} />,
    },
  ];

  useEffect(() => {
    getTradeList();
  }, []);

  useEffect(() => {
    if (BOM.HasPermission?.("CREATE_TRADE")) {
      rightAction(cnt.RIGHT_ADD_BTN);
    }
  }, []);

  const getTradeList = () => {
    setLoading(true);

    const body = { session };
    const url = `${apiConfig.GetTrades}/trades`;

    BOM.FetchReqAction(body, url, (err, res) => {
      if (err) {
        BOM.AlertMsg(
          cnt.DANGER,
          err?.defaultUserMessage || "Failed to load trades",
        );
        setLoading(false);
        return;
      }

      const mapped = (res || []).map((el) => ({
        key: el.tradeId,
        id: el.tradeId,
        tradeReference: el.tradeReference,
        portfolioId: el.portfolioId,
        counterpartyId: el.counterpartyId,
        securityId: el.securityId,
        directionDescription: el.directionDescription,
        faceValue: el.faceValue,
        price: el.price,
        yield: el.yield,
        tradeDate: formatDateArray(el.tradeDate),
        settlementDate: formatDateArray(el.settlementDate),
        status: el.status,
        statusDescription: el.statusDescription,
      }));

      setOriginalList(mapped);
      setSelectedRow([]);
      setCurrentPage(1);
      setLoading(false);
    });
  };

  const handleSearchInTable = (e) => {
    setSearchTerm(e.target.value || "");
    setCurrentPage(1);
  };

  const filteredData = useMemo(() => {
    if (!searchTerm) return originalList;
    const q = searchTerm.toLowerCase();
    return originalList.filter(
      (item) =>
        item.tradeDate?.toLowerCase().includes(q) ||
        item.tradeReference?.toLowerCase().includes(q) ||
        item.directionDescription?.toLowerCase().includes(q) ||
        item.statusDescription?.toLowerCase().includes(q),
    );
  }, [originalList, searchTerm]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const itemRender = (_, type, el) => {
    if (type === "prev" || type === "next") return;
    return el;
  };

  const onSelectItemAction = (selectedRowKeys, selectedRows) => {
    if (!selectedRowKeys || selectedRowKeys.length !== 1) {
      setSelectedRow([]);
      dispatch({ type: cnt.CLEAR_RIGHT_BTN });
      if (BOM.HasPermission?.("CREATE_TRADE")) {
        dispatch({ type: cnt.RIGHT_ADD_BTN });
      }
      return;
    }

    setSelectedRow(selectedRows);

    if (BOM.HasPermission?.("READ_TRADE"))
      dispatch({ type: cnt.RIGHT_DETAIL_BTN });
    if (BOM.HasPermission?.("UPDATE_TRADE"))
      dispatch({ type: cnt.RIGHT_EDIT_BTN });
  };

  const handleOnView = () => {
    if (rightBtn?.detailBtn === "primary")
      navigate("/view-trade", { state: selectedRow[0]?.id });
  };
  const handleOnCreate = () => {
    if (rightBtn?.addBtn === "primary") navigate("/add-trade");
  };
  const handleOnEdit = () => {
    if (rightBtn?.editBtn === "primary")
      navigate("/edit-trade", { state: selectedRow[0]?.id });
  };

  return (
    <MainLayout
      isEnableRightSidebar
      onView={handleOnView}
      onAdd={handleOnCreate}
      onEdit={handleOnEdit}
      pageItems={staticConfig.manageTrade}
    >
      <TopContent
        pageTitle={labelConfig.trade}
        createPageTitle={labelConfig.tradeListing}
      />

      <Card
        style={{ border: "1px solid #EAECF0", borderRadius: 20, marginTop: 12 }}
      >
        <Spin spinning={loading}>
          <Row
            style={{
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Form form={form}>
              <Space size={12}>
                <Input
                  value={searchTerm}
                  onChange={handleSearchInTable}
                  placeholder="Search trades…"
                  prefix={<SearchLg width={18} color="#667085" />}
                  style={{ width: 350, borderRadius: 10 }}
                />
              </Space>
            </Form>
          </Row>

          <Table
            rowSelection={{
              type: "checkbox",
              hideSelectAll: true,
              onChange: onSelectItemAction,
            }}
            columns={columns}
            dataSource={paginatedData}
            pagination={false}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20,
            }}
          >
            <Button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            >
              <ArrowLeft /> Previous
            </Button>

            <Pagination
              current={currentPage}
              total={filteredData.length}
              pageSize={pageSize}
              showSizeChanger={false}
              itemRender={itemRender}
              onChange={setCurrentPage}
            />

            <Button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            >
              Next <ArrowRight />
            </Button>
          </div>
        </Spin>
      </Card>
    </MainLayout>
  );
};

const mapStateToProps = (state) => ({ rightBtn: state.RightSideBtn });
export default connect(mapStateToProps, { rightAction })(ManageTrade);
